<template>
  <el-drawer
    v-model="visible"
    :title="user ? `${user.username} 的个人信息` : '加载中...'"
    direction="rtl"
    :size="isMobile ? '100%' : '500px'"
    :with-header="true"
    :destroy-on-close="true"
    :modal="true"
    :append-to-body="true"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <template #header>
      <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
        <span>{{ user ? `${user.username} 的个人信息` : '加载中...' }}</span>
        <el-button 
          v-if="currentUserRole == 2" 
          type="primary" 
          size="small" 
          @click="showAddCommentDialog = true"
        >
          <el-icon><Plus /></el-icon>
          添加评论
        </el-button>
      </div>
    </template>
    <div class="user-profile-container" v-loading="loading">
      <template v-if="!isLogin">
        <div class="not-login-tip">
          <el-empty description="请先登录后再查看用户信息">
            <template #image>
              <el-icon :size="60" color="var(--el-color-warning)"><Warning /></el-icon>
            </template>
            <template #description>
              <p class="login-tip-text">请先登录后再查看用户信息</p>
            </template>
            <el-button type="primary" @click="handleLogin">去登录</el-button>
          </el-empty>
        </div>
      </template>
      <template v-else-if="user">
        <!-- 用户基本信息表格 -->
        <el-table :data="[user]" style="width: 100%" border>
          <el-table-column prop="username" label="姓名" width="60"/>
          <el-table-column prop="studentId" label="学号"  width="100"/>
          <el-table-column prop="subject" label="专业" width="40"/>
          <el-table-column label="角色">
            <template #default="{ row }">
              <el-tag :type="row.role === 2 ? 'danger' : 'success'" size="small">
                {{ row.role === 2 ? '管理员' : '学生' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作"  v-if="currentUserRole === 2">
            <template #default>
              <el-button type="primary" size="small" @click="showEditDialog = true">编辑</el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 任务统计数据表格 -->
        <el-table :data="[taskStats]" style="width: 100%" border v-if="taskStats">
          <el-table-column label="总任务数" prop="taskCount" width="60"/>
          <el-table-column label="已完成" prop="completeTaskCount" width="100"/>
          <el-table-column label="点名得分" prop="callScore" width="40"/>
          <el-table-column label="点名次数" prop="callCount" width="70"/>
          <el-table-column label="总得分" prop="totalScore" />
        </el-table>

        <!-- 编辑对话框 -->
        <el-dialog
          title="编辑学生信息"
          v-model="showEditDialog"
          width="400px"
          :close-on-click-modal="false"
          :fullscreen="isMobile"
        >
          <el-form :model="editForm" :rules="editRules" ref="editFormRef" label-width="80px">
            <el-form-item label="姓名" prop="username">
              <el-input v-model="editForm.username" placeholder="请输入姓名" />
            </el-form-item>
            <el-form-item label="学号" prop="studentId">
              <el-input v-model="editForm.studentId" placeholder="请输入学号" />
            </el-form-item>
            <el-form-item label="学校" prop="school">
              <el-input v-model="editForm.school" placeholder="请输入学校" />
            </el-form-item>
          </el-form>
          <template #footer>
            <span class="dialog-footer">
              <el-button @click="showEditDialog = false">取消</el-button>
              <el-button type="primary" @click="submitEdit" :loading="editLoading">确认</el-button>
            </span>
          </template>
        </el-dialog>

        <!-- 任务列表卡片 -->
        <el-card class="task-list-card" shadow="hover">
          <template #header>
            <div class="card-header">
              <span>任务完成记录</span>
            </div>
          </template>
          <div class="task-list">
            <el-empty v-if="!taskList.length" description="暂无任务记录" />
            <div v-else class="task-items">
              <div v-for="task in taskList" :key="task.userTaskId" class="task-item">
                <div class="task-item-header">
                  <span class="task-id">任务 {{ task.orderNum }}:{{ task.title }}</span>
                  <span class="task-time">{{ formatDate(task.subTime) }}</span>
                </div>
                <div class="task-screenshot-wrapper" v-if="task.screenshot">
                  <el-image
                    :src="task.screenshot"
                    :preview-src-list="[task.screenshot]"
                    fit="cover"
                    class="task-screenshot"
                    :initial-index="0"
                    preview-teleported
                  >
                    <template #error>
                      <div class="image-error">
                        <el-icon><Picture /></el-icon>
                        <span>加载失败</span>
                      </div>
                    </template>
                  </el-image>
                </div>
              </div>
            </div>
          </div>
        </el-card>

        <!-- 评论记录卡片 - 仅管理员可见 -->
        <el-card v-if="currentUserRole === 2" class="comment-list-card" shadow="hover">
          <template #header>
            <div class="card-header">
              <span>评论记录</span>
            </div>
          </template>
          <div class="comment-list">
            <el-empty v-if="!commentList.length" description="暂无评论记录" />
            <div v-else class="comment-items">
              <div v-for="comment in commentList" :key="comment.id" class="comment-item">
                <div class="comment-item-header">
                  <div class="comment-info">
                    <span class="comment-type">{{ comment.type }}</span>
                    <span class="comment-student">{{ comment.username }}</span>
                  </div>
                  <div class="comment-actions">
                    <span class="comment-time">{{ formatDate(comment.createTime) }}</span>
                    <div v-if="currentUserRole == 2" class="action-buttons">
                      <el-button 
                        type="primary" 
                        size="small" 
                        @click="handleEditComment(comment)"
                      >
                        <el-icon><Edit /></el-icon>
                      </el-button>
                      <el-button 
                        type="danger" 
                        size="small" 
                        @click="handleDeleteComment(comment.id)"
                      >
                        <el-icon><Delete /></el-icon>
                      </el-button>
                    </div>
                  </div>
                </div>
                <div class="comment-content" v-if="comment.content">
                  <p>{{ comment.content }}</p>
                </div>
                <div class="comment-image-wrapper" v-if="comment.img">
                  <el-image
                    :src="comment.img"
                    :preview-src-list="[comment.img]"
                    fit="cover"
                    class="comment-image"
                    :initial-index="0"
                    preview-teleported
                  >
                    <template #error>
                      <div class="image-error">
                        <el-icon><Picture /></el-icon>
                        <span>加载失败</span>
                      </div>
                    </template>
                  </el-image>
                </div>
              </div>
            </div>
          </div>
        </el-card>
      </template>
      <template v-else-if="!loading">
        <div class="not-login-tip">
          <el-empty description="未找到用户信息" />
        </div>
      </template>
    </div>

    <!-- 添加评论对话框 -->
    <el-dialog
      :title="isEditMode ? '编辑评论' : '添加评论'"
      v-model="showAddCommentDialog"
      width="500px"
      :close-on-click-modal="false"
      :fullscreen="isMobile"
    >
      <el-form
        ref="addCommentFormRef"
        :model="addCommentForm"
        :rules="addCommentRules"
        label-width="80px"
      >
        <el-form-item label="学生姓名">
          <el-input :model-value="user?.username" disabled />
        </el-form-item>
        <el-form-item label="评论内容" prop="content">
          <el-input
            v-model="addCommentForm.content"
            type="textarea"
            :rows="3"
            placeholder="请输入评论内容"
          />
        </el-form-item>
        <el-form-item label="类型" prop="type">
          <el-select v-model="addCommentForm.type" placeholder="请选择类型" style="width: 100%">
            <el-option label="记录" value="记录" />
            <el-option label="头像" value="头像" />
            <el-option label="其他" value="其他" />
          </el-select>
        </el-form-item>
        <el-form-item label="图片">
          <el-upload
            ref="commentUploadRef"
            action="http://tuku.yangeit.cn:21012/api/index.php"
            :headers="uploadHeaders"
            :auto-upload="true"
            :limit="1"
            accept="image/*"
            :before-upload="handleBeforeUpload"
            :on-exceed="handleCommentExceed"
            :on-success="handleCommentUploadSuccess"
            :on-error="handleCommentUploadError"
            :on-remove="handleCommentRemoveUpload"
            :file-list="commentFileList"
            :data="{ token: 'd3e42cb544a656b7441a82aa5a414bf4'}"
            name="image"
            list-type="picture-card"
          >
            <el-icon><Plus /></el-icon>
            <template #tip>
              <div class="el-upload__tip">
                只能上传1张 jpg/png文件，且不超过5MB
              </div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showAddCommentDialog = false">取消</el-button>
          <el-button 
            type="primary" 
            @click="submitAddComment"
            :loading="addingComment"
          >
            提交
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 删除评论确认对话框 -->
    <el-dialog
      v-model="showDeleteCommentDialog"
      title="删除评论"
      width="400px"
      :close-on-click-modal="false"
     
      class="delete-confirm-dialog"
    >
      <div class="delete-confirm-content">
        <el-icon class="warning-icon"><Warning /></el-icon>
        <p>确定要删除这条评论吗？</p>
        <p class="warning-text">此操作不可恢复，请谨慎操作！</p>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showDeleteCommentDialog = false">取消</el-button>
          <el-button type="danger" @click="confirmDeleteComment" :loading="deletingComment">
            确定删除
          </el-button>
        </span>
      </template>
    </el-dialog>
  </el-drawer>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { Picture, Warning, Plus, Edit, Delete } from '@element-plus/icons-vue';
import apiClient from '../api';
import { useStore } from 'vuex';
import imageCompression from 'browser-image-compression';

export default {
  name: 'UserProfileDrawer',
  components: {
    Picture,
    Warning,
    Plus,
    Edit,
    Delete
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    userId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['update:modelValue', 'showLogin'],
  setup(props, { emit }) {
    const store = useStore();
    const loading = ref(false);
    const userInfo = ref(null);
    const taskList = ref([]);
    const taskStats = ref(null);
    const commentList = ref([]);
    const isMobile = ref(window.innerWidth < 768);

    // 编辑相关
    const showEditDialog = ref(false);
    const editForm = ref({
      username: '',
      studentId: '',
      school: '',
      userId: '',
    });
    const editFormRef = ref(null);
    const editLoading = ref(false);
    const editRules = {
      username: [
        { required: true, message: '请输入姓名', trigger: 'blur' },
      ],
      studentId: [
        { required: true, message: '请输入学号', trigger: 'blur' },
        { pattern: /^\d{1,20}$/, message: '学号是1-20位数字', trigger: 'blur' }
      ],
      school: [
        { required: true, message: '请输入学校', trigger: 'blur' },
      ],
    };

    // 添加评论相关
    const showAddCommentDialog = ref(false);
    const addCommentForm = ref({
      content: '',
      type: '记录',
      img: ''
    });
    const addCommentFormRef = ref(null);
    const addingComment = ref(false);
    const addCommentRules = {
      content: [
        { required: true, message: '请输入评论内容', trigger: 'blur' },
      ],
      type: [
        { required: true, message: '请选择类型', trigger: 'change' },
      ],
    };
    const commentFileList = ref([]);
    const commentUploadRef = ref(null);
    const uploadHeaders = computed(() => {
      const token = store.state.userInfo ? store.state.userInfo.jwtToken : null;
      return token ? { Authorization: `Bearer ${token}` } : {};
    });

    // 计算属性：检查是否已登录
    const isLogin = computed(() => {
      const loginState = store.state.isLogin;
      console.log('当前登录状态:', loginState);
      return loginState;
    });

    // 删除和编辑评论相关
    const showDeleteCommentDialog = ref(false);
    const deletingComment = ref(false);
    const commentToDelete = ref(null);
    const isEditMode = ref(false);

    // 监听抽屉显示状态和 userId 变化
    watch(
      [() => props.modelValue, () => props.userId],
      async ([isVisible, userId]) => {
        console.log('抽屉状态变化:', { isVisible, userId, isLogin: isLogin.value });
        
        if (isVisible && userId) {
          if (!isLogin.value) {
            ElMessage.warning('请先登录后再查看用户信息');
            return;
          }

          try {
            if (!userInfo.value) {
              console.log('开始获取用户信息, userId:', userId);
              await fetchUserInfo(userId);
            }
            if (!taskList.value.length) {
              console.log('开始获取任务列表, userId:', userId);
              await fetchUserTasks(userId);
            }
            if (!taskStats.value) {
              console.log('开始获取任务统计, userId:', userId);
              await fetchTaskStats(userId);
            }
            if (!commentList.value.length && currentUserRole.value === 2) {
              console.log('开始获取评论记录, userId:', userId);
              await fetchCommentList(userId);
            }
            // 自动填充编辑表单
            if (userInfo.value) {
              editForm.value = {
                username: userInfo.value.username,
                studentId: userInfo.value.studentId,
                school: userInfo.value.school || '',
                userId: userInfo.value.userId,
              };
            }
          } catch (error) {
            console.error('获取数据失败:', error);
            ElMessage.error('获取数据失败，请稍后重试');
          }
        }
      },
      { immediate: true }
    );

    // 处理登录按钮点击
    const handleLogin = () => {
      emit('showLogin');
      emit('update:modelValue', false); // 关闭抽屉
    };

    // 获取用户信息
    const fetchUserInfo = async (userId) => {
      if (!userId || userInfo.value) return;
      
      loading.value = true;
      try {
        console.log('发送用户信息请求:', `/api/user/info?userId=${userId}`);
        const response = await apiClient.get(`/user/info?userId=${userId}`);
        console.log('用户信息响应:', response);
        
        if (response.code === '000000') {
          userInfo.value = response.data;
          console.log('成功获取用户信息:', userInfo.value);
        } else {
          ElMessage.error('获取用户信息失败：' + response.msg);
        }
      } catch (error) {
        console.error('获取用户信息错误:', error);
        ElMessage.error('获取用户信息异常：' + (error.response?.data?.msg || error.message));
      } finally {
        loading.value = false;
      }
    };

    // 获取用户任务列表
    const fetchUserTasks = async (userId) => {
      if (!userId) return;
      
      try {
        console.log('发送任务列表请求:', `/api/usertask/info?userId=${userId}`);
        const response = await apiClient.get(`/usertask/info?userId=${userId}`);
        console.log('任务列表响应:', response);
        
        if (response.code === '000000') {
          taskList.value = response.data;
          console.log('成功获取任务列表:', taskList.value);
        } else {
          ElMessage.error('获取任务列表失败：' + response.msg);
        }
      } catch (error) {
        console.error('获取任务列表错误:', error);
        ElMessage.error('获取任务列表异常：' + (error.response?.data?.msg || error.message));
      }
    };

    // 获取任务统计数据
    const fetchTaskStats = async (userId) => {
      if (!userId) return;
      
      try {
        console.log('发送任务统计请求:', `/api/user/getUserTaskCount?userId=${userId}`);
        const response = await apiClient.get(`/user/getUserTaskCount?userId=${userId}`);
        console.log('任务统计响应:', response);
        
        if (response.code === '000000') {
          taskStats.value = response.data;
          console.log('成功获取任务统计:', taskStats.value);
        } else {
          ElMessage.error('获取任务统计失败：' + response.msg);
        }
      } catch (error) {
        console.error('获取任务统计错误:', error);
        ElMessage.error('获取任务统计异常：' + (error.response?.data?.msg || error.message));
      }
    };

    // 获取评论记录
    const fetchCommentList = async (userId) => {
      if (!userId || !userInfo.value) return;
      
      try {
        const studentId = userInfo.value.studentId;
        console.log('发送评论记录请求:', `/api/user/records/list?studentId=${studentId}`);
        const response = await apiClient.get(`/user/records/list?studentId=${studentId}`);
        console.log('评论记录响应:', response);
        
        if (response.code === '000000') {
          commentList.value = response.data;
          console.log('成功获取评论记录:', commentList.value);
        } else {
          ElMessage.error('获取评论记录失败：' + response.msg);
        }
      } catch (error) {
        console.error('获取评论记录错误:', error);
        ElMessage.error('获取评论记录异常：' + (error.response?.data?.msg || error.message));
      }
    };

    // 编辑提交
    const submitEdit = async () => {
      if (!editFormRef.value) return;
      await editFormRef.value.validate(async (valid) => {
        if (valid) {
          editLoading.value = true;
          try {
            const params = {
              school: editForm.value.school,
              studentId: editForm.value.studentId,
              userId: editForm.value.userId,
              username: editForm.value.username,
            };
            const response = await apiClient.put('/user/update', params);
            if (response.code === '000000') {
              ElMessage.success('信息修改成功');
              showEditDialog.value = false;
              // 重新获取用户信息
              userInfo.value = null;
              await fetchUserInfo(params.userId);
            } else {
              throw new Error(response.msg || '修改失败');
            }
          } catch (error) {
            ElMessage.error('修改失败: ' + (error.response?.data?.msg || error.message));
          } finally {
            editLoading.value = false;
          }
        }
      });
    };

    const visible = computed({
      get: () => props.modelValue,
      set: (value) => {
        if (!value) {
          // 关闭抽屉时重置数据
          userInfo.value = null;
          taskList.value = [];
          taskStats.value = null;
          commentList.value = [];
        }
        emit('update:modelValue', value);
      }
    });

    const handleClose = () => {
      emit('update:modelValue', false);
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      //时间是一个数组，取第一个元素
      return new Date(dateStr).toLocaleString();
    };

    // 获取当前登录用户的角色
    const currentUserRole = computed(() => {
      const user = store.getters.userInfo;
      return user ? user.role : null;
    });

    // 添加评论相关方法
    const handleCommentExceed = () => {
      ElMessage.warning('只能上传一张图片');
    };

    const handleCommentUploadSuccess = (response, file) => {
      if (response.result === 'success' && response.code === 200) {
        ElMessage.success('图片上传成功');
        addCommentForm.value.img = response.url;
        // 保持UI同步
        file.url = response.url;
        commentFileList.value = [file];
      } else {
        ElMessage.error('图片上传失败: ' + (response.msg || '未知错误'));
        commentUploadRef.value.clearFiles();
        addCommentForm.value.img = '';
      }
    };

    const handleCommentUploadError = (error) => {
      console.error('上传错误:', error);
      ElMessage.error('图片上传失败，请重试');
      commentUploadRef.value.clearFiles();
      addCommentForm.value.img = '';
    };

    const handleCommentRemoveUpload = () => {
      commentFileList.value = [];
      addCommentForm.value.img = '';
    };

    const submitAddComment = async () => {
      if (!addCommentFormRef.value) return;
      await addCommentFormRef.value.validate(async (valid) => {
        if (valid) {
          addingComment.value = true;
          try {
            const params = {
              content: addCommentForm.value.content,
              type: addCommentForm.value.type,
              studentId: userInfo.value.studentId,
              username: userInfo.value.username,
              img: addCommentForm.value.img || ''
            };
            
            let response;
            if (isEditMode.value) {
              // 编辑模式，调用更新接口
              params.id = commentToDelete.value;
              response = await apiClient.put('/user/records/update', params);
            } else {
              // 添加模式，调用添加接口
              response = await apiClient.post('/user/records/add', params);
            }
            
            if (response.code === '000000') {
              ElMessage.success(isEditMode.value ? '评论更新成功' : '评论添加成功');
              showAddCommentDialog.value = false;
              // 重置表单
              addCommentForm.value = {
                content: '',
                type: '记录',
                img: ''
              };
              commentFileList.value = [];
              isEditMode.value = false;
              commentToDelete.value = null;
              // 重新获取评论记录
              commentList.value = [];
              await fetchCommentList(userInfo.value.userId);
            } else {
              throw new Error(response.msg || (isEditMode.value ? '更新失败' : '评论添加失败'));
            }
          } catch (error) {
            ElMessage.error((isEditMode.value ? '更新评论失败' : '评论添加失败') + ': ' + (error.response?.data?.msg || error.message));
          } finally {
            addingComment.value = false;
          }
        }
      });
    };

    const handleEditComment = (comment) => {
      // 设置编辑模式
      isEditMode.value = true;
      // 填充表单数据
      addCommentForm.value = {
        content: comment.content,
        type: comment.type,
        img: comment.img || ''
      };
      // 设置图片文件列表
      if (comment.img) {
        commentFileList.value = [{
          name: '评论图片',
          url: comment.img
        }];
      } else {
        commentFileList.value = [];
      }
      // 保存要编辑的评论ID
      commentToDelete.value = comment.id;
      // 显示对话框
      showAddCommentDialog.value = true;
    };

    const handleDeleteComment = (commentId) => {
      commentToDelete.value = commentId;
      showDeleteCommentDialog.value = true;
    };

    const confirmDeleteComment = async () => {
      if (!commentToDelete.value) return;
      
      try {
        deletingComment.value = true;
        const response = await apiClient.delete(`/user/records/delete?recordId=${commentToDelete.value}`);
        if (response.code === '000000') {
          ElMessage.success('评论删除成功');
          showDeleteCommentDialog.value = false;
          // 重新获取评论记录
          commentList.value = [];
          await fetchCommentList(userInfo.value.userId);
        } else {
          throw new Error(response.msg || '删除失败');
        }
      } catch (error) {
        ElMessage.error('删除评论失败: ' + (error.response?.data?.msg || error.message));
      } finally {
        deletingComment.value = false;
        commentToDelete.value = null;
      }
    };

    const handleBeforeUpload = async (file) => {
      // 只处理图片
      if (!file.type.startsWith('image/')) return false;
      // 小于1MB不压缩
      if (file.size < 2*1024 * 1024) return true;
      const options = {
        maxSizeMB: 1, // 压缩到0.5MB以内
        maxWidthOrHeight: 1980, // 最大宽高
        useWebWorker: true,
      };
      try {
        const compressedFile = await imageCompression(file, options);
        return compressedFile;
      } catch (e) {
        ElMessage.error('图片压缩失败');
        return false;
      }
    };

    return {
      visible,
      user: computed(() => {
        console.log('当前用户信息:', userInfo.value);
        return userInfo.value;
      }),
      loading,
      isMobile,
      taskList,
      taskStats,
      commentList,
      isLogin,
      handleClose,
      handleLogin,
      formatDate,
      // 编辑相关
      showEditDialog,
      editForm,
      editFormRef,
      editLoading,
      editRules,
      submitEdit,
      currentUserRole,
      // 添加评论相关
      showAddCommentDialog,
      addCommentForm,
      addCommentFormRef,
      addingComment,
      addCommentRules,
      commentFileList,
      commentUploadRef,
      uploadHeaders,
      handleCommentExceed,
      handleCommentUploadSuccess,
      handleCommentUploadError,
      handleCommentRemoveUpload,
      submitAddComment,
      handleEditComment,
      handleDeleteComment,
      confirmDeleteComment,
      // 删除和编辑评论相关
      showDeleteCommentDialog,
      deletingComment,
      commentToDelete,
      isEditMode,
      handleBeforeUpload,
    };
  }
};
</script>

<style scoped>
.user-profile-container {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  background-color: var(--el-bg-color);
}

/* 自定义抽屉样式 */
:deep(.el-drawer) {
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
}

:deep(.el-drawer__header) {
  margin-bottom: 0;
  padding: 16px 20px;
  border-bottom: 1px solid var(--el-border-color-light);
  font-size: 16px;
  font-weight: 600;
}

:deep(.el-drawer__body) {
  padding: 0;
  height: calc(100% - 55px);
  overflow: hidden;
}

/* 表格样式优化 */
:deep(.el-table) {
  --el-table-border-color: var(--el-border-color-light);
  --el-table-header-bg-color: var(--el-fill-color-light);
}

:deep(.el-table th) {
  font-weight: 600;
  background-color: var(--el-fill-color-light);
}

:deep(.el-table td) {
  padding: 8px 0;
}

.task-list-card {
  margin-bottom: 0;
  border-radius: 8px;
  border: none;
  background-color: var(--el-bg-color-overlay);
}

.comment-list-card {
  margin-bottom: 0;
  border-radius: 8px;
  border: none;
  background-color: var(--el-bg-color-overlay);
}

:deep(.el-card__header) {
  padding: 15px 20px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  border-bottom: 1px solid var(--el-border-color-light);
}

.task-list {
  max-height: calc(100vh - 400px);
  overflow-y: auto;
  padding: 5px;
}

.comment-list {
  max-height: calc(100vh - 400px);
  overflow-y: auto;
  padding: 5px;
}

.task-items {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.comment-items {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.comment-item {
  border: 1px solid var(--el-border-color-light);
  border-radius: 6px;
  padding: 12px;
  transition: all 0.3s ease;
  background-color: var(--el-bg-color);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.comment-item:hover {
  border-color: var(--el-color-primary-light-7);
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.comment-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.comment-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

.comment-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.comment-type {
  background-color: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: 500;
}

.comment-student {
  font-weight: 500;
  color: var(--el-text-color-primary);
}

.comment-time {
  font-size: 0.9em;
  color: var(--el-text-color-secondary);
}

.comment-content {
  color: var(--el-text-color-primary);
  line-height: 1.5;
}

.comment-content p {
  margin: 0;
}

.comment-image-wrapper {
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--el-border-color-lighter);
  width: 120px;
  align-self: flex-start;
}

.comment-image {
  width: 120px;
  height: 120px;
  cursor: pointer;
  transition: transform 0.3s ease;
  object-fit: cover;
}

.comment-image:hover {
  transform: scale(1.02);
}

.task-item {
  border: 1px solid var(--el-border-color-light);
  border-radius: 6px;
  padding: 12px;
  transition: all 0.3s ease;
  background-color: var(--el-bg-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.task-item:hover {
  border-color: var(--el-color-primary-light-7);
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.task-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 0;
}

.task-id {
  font-weight: 500;
  color: var(--el-text-color-primary);
  font-size: 1.05em;
}

.task-time {
  font-size: 0.9em;
  color: var(--el-text-color-secondary);
}

.task-screenshot-wrapper {
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--el-border-color-lighter);
  width: 120px;
  margin: 0 auto;
}

.task-screenshot {
  width: 120px;
  height: 120px;
  cursor: pointer;
  transition: transform 0.3s ease;
  object-fit: cover;
}

.task-screenshot:hover {
  transform: scale(1.02);
}

.image-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 120px;
  color: var(--el-text-color-secondary);
  gap: 8px;
  background-color: var(--el-fill-color-light);
}

/* 移动端适配 */
@media (max-width: 768px) {
  :deep(.el-drawer) {
    width: 100% !important;
  }

  .user-profile-container {
    padding: 15px;
    gap: 15px;
  }

  :deep(.el-table td),
  :deep(.el-table th) {
    padding: 6px 0;
    font-size: 14px;
  }

  .task-item {
    padding: 10px;
  }

  .task-screenshot-wrapper {
    width: 100px;
  }

  .task-screenshot {
    width: 100px;
    height: 100px;
  }

  .image-error {
    height: 100px;
  }

  .task-id {
    font-size: 0.95em;
  }

  .task-time {
    font-size: 0.85em;
  }

  .task-list {
    max-height: calc(100vh - 350px);
  }

  .comment-list {
    max-height: calc(100vh - 350px);
  }

  .comment-item {
    padding: 10px;
  }

  .comment-image-wrapper {
    width: 100px;
  }

  .comment-image {
    width: 100px;
    height: 100px;
  }

  .comment-type {
    font-size: 0.75em;
    padding: 1px 6px;
  }

  .comment-student {
    font-size: 0.9em;
  }

  .comment-time {
    font-size: 0.8em;
  }
}

.not-login-tip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px;
  text-align: center;
}

.login-tip-text {
  color: var(--el-text-color-secondary);
  margin: 12px 0;
  font-size: 14px;
}

:deep(.el-empty__image) {
  margin-bottom: 16px;
}

:deep(.el-empty__description) {
  margin-top: 8px;
}

:deep(.el-empty__bottom) {
  margin-top: 16px;
}

/* 评论上传组件样式 */
:deep(.el-upload--picture-card) {
  width: 120px;
  height: 120px;
  line-height: 120px;
}

:deep(.el-upload-list--picture-card .el-upload-list__item) {
  width: 120px;
  height: 120px;
}

/* 移动端适配 */
@media (max-width: 768px) {
  :deep(.el-upload--picture-card) {
    width: 100px;
    height: 100px;
    line-height: 100px;
  }

  :deep(.el-upload-list--picture-card .el-upload-list__item) {
    width: 100px;
    height: 100px;
  }
}
</style> 